import { Button, Card, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconEdit, IconUsers } from '@tabler/icons-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  contactRowHasGuarantorRole,
  type ClientOverviewContactRow,
} from '../utils/getClientOverviewContacts';
import { ContactRoiBadge } from './ContactRoiBadge';
import { ContactRoiModal } from './ContactRoiModal';
import type { Contact } from '../data/contactTypes';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import { usePatientRoiConsents } from '../hooks/usePatientRoiConsents';
import { contactFromOverviewRow } from '../utils/overviewContactModal';
import { resolveContactsWithRoi } from '../utils/contactRoiUtils';

function ContactNameWithRoles({
  row,
  emphasizeName,
}: {
  row: ClientOverviewContactRow;
  emphasizeName: boolean;
}): JSX.Element {
  return (
    <Group gap={4} wrap="nowrap" align="baseline" style={{ minWidth: 0, width: '100%' }}>
      <BoxGrow>
        <Text size="sm" fw={600} c={emphasizeName ? 'gold.7' : undefined} truncate>
          {row.displayName}
        </Text>
      </BoxGrow>
      <Text span size="sm" c="dimmed" style={{ flexShrink: 1, minWidth: 0 }} truncate>
        ({row.rolesSummary})
      </Text>
    </Group>
  );
}

/** Flex child so name ellipsis works inside a row group */
function BoxGrow({ children }: { children: ReactNode }): JSX.Element {
  return <div style={{ minWidth: 0, flex: '1 1 40%' }}>{children}</div>;
}

function formatContactLine(value: string | undefined): string {
  const t = value?.trim();
  return t || '—';
}

function LabeledContactLine({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Group gap={6} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
      <Text component="span" size="xs" c="gray.6" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="xs" c="gray.6" truncate style={{ minWidth: 0, flex: 1 }}>
        {value}
      </Text>
    </Group>
  );
}

function rowEmphasizeName(row: ClientOverviewContactRow): boolean {
  if (row.clientChartId) return true;
  if (contactRowHasGuarantorRole(row) && row.guarantorBillingProfileClientId) return true;
  return false;
}

export interface ClientChartContactsPanelProps {
  clientId: string;
  /** Precomputed from `getClientOverviewContacts`; defaults to [] if omitted */
  rows?: ClientOverviewContactRow[];
  onRowActivate: (row: ClientOverviewContactRow, contact: Contact) => void;
  localhostOnly?: boolean;
}

/**
 * Episode-of-care contacts (guarantors, couples/family participants) for the Medical Record sidebar.
 * Entire card is clickable: client rows navigate to chart; non-clients open the profile contact modal (parent).
 */
export function ClientChartContactsPanel({
  clientId,
  rows = [],
  onRowActivate,
  localhostOnly,
}: ClientChartContactsPanelProps): JSX.Element {
  const profileContactsHref = `/dashboard/clients/${clientId}/profile?tab=contacts`;
  const { consents, refresh: refreshRoiConsents } = usePatientRoiConsents(clientId);
  const [roiContact, setRoiContact] = useState<Contact | null>(null);

  const openRoiView = (contact: Contact) => {
    setRoiContact(contact);
  };

  const closeRoi = () => setRoiContact(null);

  const contactsByKey = useMemo(() => {
    const base = rows.map((row) => contactFromOverviewRow(row));
    const enriched = resolveContactsWithRoi(base, consents);
    return new Map(rows.map((row, index) => [row.dedupeKey, enriched[index]!]));
  }, [rows, consents]);

  return (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <IconUsers size={18} color="gray" style={{ flexShrink: 0 }} />
            <Title order={4}>Contacts</Title>
            {localhostOnly ? <LocalhostOnlyBadge /> : null}
          </Group>
          <Button
            component={Link}
            to={profileContactsHref}
            leftSection={<IconEdit size={14} />}
            size="sm"
            style={{ flexShrink: 0 }}
          >
            Edit
          </Button>
        </Group>
        {rows.length === 0 ? (
          <Text c="gray.6" size="sm">
            No related contacts on file for this client.
          </Text>
        ) : (
          <Stack gap="sm">
            {rows.map((row) => {
              const contact = contactsByKey.get(row.dedupeKey) ?? contactFromOverviewRow(row);
              return (
                <Card
                  key={row.dedupeKey}
                  withBorder
                  padding="md"
                  role="button"
                  tabIndex={0}
                  onClick={() => onRowActivate(row, contact)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowActivate(row, contact);
                    }
                  }}
                  style={{ cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--mantine-color-gold-0)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                  }}
                >
                  <Stack gap={6}>
                    <ContactNameWithRoles row={row} emphasizeName={rowEmphasizeName(row)} />
                    <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
                      <Text size="xs" c="gray.6" truncate style={{ minWidth: 0, flex: 1 }}>
                        {row.episodesOfCareSummary}
                      </Text>
                      <ContactRoiBadge
                        contact={contact}
                        onView={() => openRoiView(contact)}
                      />
                    </Group>
                    <LabeledContactLine label="Phone:" value={formatContactLine(row.phone)} />
                    <LabeledContactLine label="Email:" value={formatContactLine(row.email)} />
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>

      {roiContact && (
        <ContactRoiModal
          opened={roiContact !== null}
          onClose={closeRoi}
          contact={roiContact}
          patientId={clientId}
          onSaved={async () => {
            closeRoi();
            await refreshRoiConsents();
          }}
        />
      )}
    </Paper>
  );
}
